// src/pages/clients/AddEditClient.js
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { es } from 'date-fns/locale';
import React, { useState, useEffect } from 'react';
import ClientEquipment from '../../components/clients/ClientEquipment';
import ClientAdditionalServices from '../../components/clients/ClientAdditionalServices';
import ClientDocumentsTab from '../../components/documents/ClientDocumentsTab';
import CoordinateSelector from '../../components/maps/CoordinateSelector';
import { useParams, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Tooltip,
  FormControlLabel,
  Checkbox,
  Divider,
  Autocomplete,
  Switch
} from '@mui/material';
import {
  Save as SaveIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Info as InfoIcon,
  Description as DescriptionIcon,
  Block as BlockIcon
} from '@mui/icons-material';
import ContractModal from '../../components/contracts/ContractModal';

const TabPanel = ({ children, value, index }) => (
  <div hidden={value !== index} className="p-4">
    {value === index && children}
  </div>
);

// Función para validar la seguridad de la contraseña
const validatePassword = (password) => {
  if (!password) return { valid: true }; // La contraseña es opcional en la creación/edición

  if (password.length < 6) {
    return { valid: false, message: 'La contraseña debe tener al menos 6 caracteres' };
  }

  // Opcionalmente podríamos agregar más validaciones
  return { valid: true };
};

const AddEditClient = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const zoneId = searchParams.get('zone');
  const cloneFrom = searchParams.get('cloneFrom');

  const [activeTab, setActiveTab] = useState(0);
  const [formData, setFormData] = useState({
    queueName: '',
    firstName: '',
    lastName: '',
    dni: '',
    email: '',
    phone: '',
    address: '',
    ipAddress: '',
    macAddress: '',
    coordinates: '',
    routerId: '',
    planId: '',
    zoneId: zoneId || '',
    nodeId: '',
    sectorialId: '',
    clientPortalPassword: '',
    antennaModel: '',
    antennaUser: 'admin',
    antennaPassword: '',
    antennaMac: '',
    wifiRouterModel: '',
    wifiRouterUser: 'admin',
    wifiRouterPassword: '',
    wifiRouterMac: '',
    wifiSsid: '',
    wifiPassword: '',
    department: '',
    province: '',
    district: '',
    // Nuevos campos para el ciclo de facturación
    billingDay: null,
    paymentDay: null,
    billingTime: '00:00',
    paymentTime: '00:00',
    dueDate: null,
    useZoneBillingCycle: true, // Checkbox para usar el ciclo de la zona o personalizar
    document_type: 'DNI',
    business_name: '',
    suspensionInvoiceThreshold: 'global',
    discountAmount: 0.00,
    discountMonths: 0,
    is_courtesy: false,
    courtesy_reason: '',
    cloneFromClientId: ''
  });

  // Estados para los datos de referencia
  const [zones, setZones] = useState([]);
  const [routers, setRouters] = useState([]);
  const [plans, setPlans] = useState([]);
  const [sectoriales, setSectoriales] = useState([]);
  const [nodes, setNodes] = useState([]);
  const [selectedZone, setSelectedZone] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [passwordWarning, setPasswordWarning] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showAntennaPassword, setShowAntennaPassword] = useState(false);
  const [showWifiRouterPassword, setShowWifiRouterPassword] = useState(false);
  const [showWifiPassword, setShowWifiPassword] = useState(false);
  const [availableIps, setAvailableIps] = useState([]);
  const [loadingIps, setLoadingIps] = useState(false);
  const [initialIp, setInitialIp] = useState('');

  // Estados para la prueba de conexión al router
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [contractModalOpen, setContractModalOpen] = useState(false);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        const [zonesRes, routersRes, plansRes, sectorialesRes, nodesRes] = await Promise.all([
          fetch('/api/zones', {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          }),
          fetch('/api/routers', {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          }),
          fetch('/api/plans', {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          }),
          fetch('/api/access-points', {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          }),
          fetch('/api/nodes', {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          })
        ]);

        const zonesData = await zonesRes.json();
        const routersData = await routersRes.json();
        const plansData = await plansRes.json();
        const sectorialesData = await sectorialesRes.json();
        const nodesData = await nodesRes.json();

        setZones(zonesData);
        setRouters(routersData);
        setPlans(plansData);
        setSectoriales(sectorialesData);
        setNodes(nodesData);

        if (id) {
          await fetchClientData(id, zonesData, sectorialesData);
        } else if (cloneFrom) {
          await fetchCloneClientData(cloneFrom);
        } else {
          // Si hay una zona preseleccionada en el query param
          if (zoneId) {
            const zone = zonesData.find(z => z.id === zoneId);
            if (zone) {
              setSelectedZone(zone);
              setFormData(prev => ({
                ...prev,
                billingDay: zone.billing_day || null,
                paymentDay: zone.payment_day || null,
                dueDate: zone.cutoff_day ? calculateDueDate(zone.cutoff_day) : prev.dueDate
              }));
            }
          }
        }
      } catch (err) {
        console.error('Error loading initial data:', err);
        setError('Error al cargar datos iniciales');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [id, cloneFrom]);

  // Nuevo efecto para manejar el cambio de zona y actualizar las fechas del ciclo
  useEffect(() => {
    if (formData.zoneId && formData.useZoneBillingCycle) {
      // Encontrar la zona seleccionada
      const zone = zones.find(z => z.id === formData.zoneId);
      if (zone) {
        setSelectedZone(zone);
        setFormData(prev => ({
          ...prev,
          billingDay: zone.billing_day || null,
          paymentDay: zone.payment_day || null,
          billingTime: zone.billing_time ? zone.billing_time.slice(0, 5) : '00:00',
          paymentTime: zone.payment_time ? zone.payment_time.slice(0, 5) : '00:00',
          // Calcular una fecha de vencimiento basada en el día de corte y la hora de corte configurada
          dueDate: zone.cutoff_day ? calculateDueDate(zone.cutoff_day, zone.cutoff_time) : prev.dueDate
        }));
      }
    }
  }, [formData.zoneId, formData.useZoneBillingCycle, zones]);

  // Función para calcular la fecha de vencimiento basada en el día y la hora
  const calculateDueDate = (day, timeStr = '00:00') => {
    const today = new Date();
    const [hours, minutes] = (timeStr || '00:00').split(':').map(Number);
    let dueDate = new Date(today.getFullYear(), today.getMonth(), day, hours || 0, minutes || 0, 0, 0);

    // Si el día ya pasó este mes, avanzar al próximo mes
    if (dueDate < today) {
      dueDate = new Date(today.getFullYear(), today.getMonth() + 1, day, hours || 0, minutes || 0, 0, 0);
    }

    return dueDate;
  };

  const fetchClientData = async (clientId, zonesList, sectorialesList) => {
    try {
      const response = await fetch(`/api/clients/${clientId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (!response.ok) {
        throw new Error('Error al obtener datos del cliente');
      }

      const data = await response.json();
      console.log('Datos recibidos del cliente:', data);

      // Determinar si el cliente usa el ciclo de facturación de la zona o tiene uno personalizado
      const useZoneBillingCycle = !data.billing_day && !data.payment_day && data.zone_id;

      // Buscar el node_id correspondiente en sectorialesList
      const apObj = sectorialesList.find(ap => ap.id === data.access_point_id);
      const clientNodeId = apObj ? apObj.node_id : '';

      setInitialIp(data.ip_address || '');

      // Mapear los datos recibidos al formato del formulario
      setFormData(prevData => ({
        ...prevData,
        queueName: data.queue_name || '',
        firstName: data.first_name || '',
        lastName: data.last_name || '',
        dni: data.dni || '',
        email: data.email || '',
        phone: data.phone || '',
        address: data.address || '',
        ipAddress: data.ip_address || '',
        macAddress: data.mac_address || '',
        coordinates: data.coordinates || '',
        routerId: data.router_id || '',
        planId: data.plan_id || '',
        zoneId: data.zone_id || '',
        nodeId: clientNodeId || '',
        sectorialId: data.access_point_id || '',
        antennaModel: data.antenna_model || '',
        antennaUser: data.antenna_user || 'admin',
        antennaPassword: data.antenna_password || '',
        antennaMac: data.antenna_mac || '',
        wifiRouterModel: data.wifi_router_model || '',
        wifiRouterUser: data.wifi_router_user || 'admin',
        wifiRouterPassword: data.wifi_router_password || '',
        wifiRouterMac: data.wifi_router_mac || '',
        wifiSsid: data.wifi_ssid || '',
        wifiPassword: data.wifi_password || '',
        clientPortalPassword: data.client_password || '',
        department: data.department || '',
        province: data.province || '',
        district: data.district || '',
        // Campos del ciclo de facturación
        billingDay: data.billing_day || data.zone_billing_day || null,
        paymentDay: data.payment_day || data.zone_payment_day || null,
        billingTime: data.billing_time ? data.billing_time.slice(0, 5) : (data.zone_billing_time ? data.zone_billing_time.slice(0, 5) : '00:00'),
        paymentTime: data.payment_time ? data.payment_time.slice(0, 5) : (data.zone_payment_time ? data.zone_payment_time.slice(0, 5) : '00:00'),
        dueDate: data.due_date ? new Date(data.due_date) : null,
        useZoneBillingCycle: useZoneBillingCycle,
        document_type: data.document_type || 'DNI',
        business_name: data.business_name || '',
        suspensionInvoiceThreshold: data.suspension_invoice_threshold !== null && data.suspension_invoice_threshold !== undefined
          ? String(data.suspension_invoice_threshold)
          : 'global',
        discountAmount: data.discount_amount !== undefined ? data.discount_amount : (data.discountAmount || 0.00),
        discountMonths: data.discount_months !== undefined ? data.discount_months : (data.discountMonths || 0),
        is_courtesy: data.is_courtesy === true || data.is_courtesy === 'true',
        courtesy_reason: data.courtesy_reason || ''
      }));

      // Actualizar la zona seleccionada
      if (data.zone_id) {
        const zone = zonesList.find(z => z.id === data.zone_id);
        if (zone) {
          setSelectedZone(zone);
        }
      }
    } catch (error) {
      console.error('Error fetching client:', error);
      setError('Error al cargar datos del cliente');
    }
  };

  // Fetch available IPs when router or AP changes
  useEffect(() => {
    const fetchAvailableIps = async () => {
      if (!formData.routerId) {
        setAvailableIps([]);
        return;
      }
      setLoadingIps(true);
      try {
        let url = `/api/routers/${formData.routerId}/available-ips`;
        if (formData.sectorialId) {
          url += `?access_point_id=${formData.sectorialId}`;
        }
        const response = await fetch(url, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (response.ok) {
          const data = await response.json();
          if (initialIp && !data.includes(initialIp)) {
            setAvailableIps([initialIp, ...data]);
          } else {
            setAvailableIps(data);
          }
        }
      } catch (err) {
        console.error('Error fetching available IPs:', err);
      } finally {
        setLoadingIps(false);
      }
    };
    fetchAvailableIps();
  }, [formData.routerId, formData.sectorialId, initialIp]);

  const fetchCloneClientData = async (cloneId) => {
    try {
      setLoading(true);
      const response = await fetch(`/api/clients/${cloneId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (!response.ok) {
        throw new Error('Error al obtener datos del cliente a clonar');
      }

      const data = await response.json();
      console.log('Datos recibidos para clonar cliente:', data);

      // Pre-rellenar los datos personales del formulario
      setFormData(prevData => ({
        ...prevData,
        firstName: data.first_name || '',
        lastName: data.last_name || '',
        dni: data.dni || '',
        email: data.email || '',
        phone: data.phone || '',
        department: data.department || '',
        province: data.province || '',
        document_type: data.document_type || 'DNI',
        business_name: data.business_name || '',
        discountAmount: data.discount_amount !== undefined ? data.discount_amount : (data.discountAmount || 0.00),
        discountMonths: data.discount_months !== undefined ? data.discount_months : (data.discountMonths || 0),
        antennaModel: data.antenna_model || '',
        antennaPassword: data.antenna_password || '',
        wifiRouterModel: data.wifi_router_model || '',
        wifiRouterPassword: data.wifi_router_password || '',
        wifiSsid: data.wifi_ssid || '',
        wifiPassword: data.wifi_password || '',
        clientPortalPassword: data.client_password || '',
        cloneFromClientId: cloneId // Guardar para enviar al backend
      }));
    } catch (error) {
      console.error('Error fetching clone client data:', error);
      setError('Error al clonar datos del cliente original');
    } finally {
      setLoading(false);
    }
  };

  const handleZoneChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      zoneId: value,
      routerId: '',
      nodeId: '',
      sectorialId: '',
      ipAddress: ''
    }));

    if (formData.useZoneBillingCycle) {
      const newSelectedZone = zones.find(z => z.id === value);
      if (newSelectedZone) {
        setSelectedZone(newSelectedZone);
      }
    }
    setTestResult(null);
    setError('');
  };

  const handleRouterChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      routerId: value,
      sectorialId: '',
      ipAddress: ''
    }));
    setTestResult(null);
    setError('');
  };

  const handleNodeChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      nodeId: value,
      sectorialId: '',
      ipAddress: ''
    }));
    setError('');
  };

  const handleAPChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      sectorialId: value,
      ipAddress: ''
    }));
    setError('');
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'useZoneBillingCycle') {
      // Si se activa "usar ciclo de zona", actualizar las fechas
      if (checked && selectedZone) {
        setFormData(prev => ({
          ...prev,
          useZoneBillingCycle: checked,
          billingDay: selectedZone.billing_day || null,
          paymentDay: selectedZone.payment_day || null,
          billingTime: selectedZone.billing_time ? selectedZone.billing_time.slice(0, 5) : '00:00',
          paymentTime: selectedZone.payment_time ? selectedZone.payment_time.slice(0, 5) : '00:00',
          dueDate: selectedZone.cutoff_day ? calculateDueDate(selectedZone.cutoff_day, selectedZone.cutoff_time) : prev.dueDate
        }));
      } else {
        // Si se desactiva, mantener los valores actuales pero permitir personalización
        setFormData(prev => ({
          ...prev,
          useZoneBillingCycle: checked
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }

    // Validar contraseña
    if (name === 'clientPortalPassword') {
      const validation = validatePassword(value);
      if (!validation.valid) {
        setPasswordWarning(validation.message);
      } else {
        setPasswordWarning('');
      }
    }
  };

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const testMikrotikConnection = async () => {
    if (!formData.routerId) {
      setError('Debe seleccionar un router para probar la conexión');
      return;
    }

    setTesting(true);
    setError('');

    try {
      const response = await fetch('/api/routers/test-connection-by-id', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: formData.routerId
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setTestResult(true);
      } else {
        setTestResult(false);
        setError(data.message || 'No se pudo establecer conexión con el router');
      }
    } catch (error) {
      setTestResult(false);
      setError('Error al probar la conexión: ' + (error.message || 'Error desconocido'));
    } finally {
      setTesting(false);
    }
  };

  const [successMessage, setSuccessMessage] = useState('');
  const [lookingUpDoc, setLookingUpDoc] = useState(false);

  const handleLookupDocument = async () => {
    if (!formData.dni || formData.dni.trim().length < 8) {
      setError('Ingrese un número de DNI (8 dígitos) o RUC (11 dígitos) válido para consultar');
      return;
    }

    setLookingUpDoc(true);
    setError('');
    setSuccessMessage('');

    try {
      const response = await fetch(`/api/clients/lookup-dni-ruc/${formData.dni.trim()}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'No se encontraron datos para el documento');
      }

      if (data.type === 'DNI') {
        setFormData(prev => ({
          ...prev,
          firstName: data.firstName || prev.firstName,
          lastName: data.lastName || prev.lastName,
          address: data.address || prev.address
        }));
        setSuccessMessage(`✅ Datos encontrados en RENIEC: ${data.fullName}`);
      } else if (data.type === 'RUC') {
        setFormData(prev => ({
          ...prev,
          business_name: data.businessName || prev.business_name,
          firstName: data.businessName ? data.businessName.split(' ')[0] : prev.firstName,
          lastName: data.businessName ? data.businessName.split(' ').slice(1).join(' ') : prev.lastName,
          address: data.address || prev.address
        }));
        setSuccessMessage(`✅ Razón Social encontrada en SUNAT: ${data.businessName} (${data.status})`);
      }
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      console.error('Error al consultar DNI/RUC:', err);
      setError(err.message || 'No se pudo consultar información del documento');
    } finally {
      setLookingUpDoc(false);
    }
  };

  const handleSubmit = async (e, redirectAfterSave = true) => {
    if (e && e.preventDefault) e.preventDefault();
    setError('');
    setSuccessMessage('');
    setSaving(true);

    // Validar contraseña antes de enviar
    if (formData.clientPortalPassword) {
      const validation = validatePassword(formData.clientPortalPassword);
      if (!validation.valid) {
        setError(validation.message);
        setSaving(false);
        return;
      }
    }

    try {
      // IMPORTANTE: Hacer coincidir exactamente los nombres de los campos con lo que el backend espera
      const dataToSubmit = {
        ...formData,
        // Renombrar campos para que coincidan con lo que espera el backend (snake_case)
        billing_day: formData.useZoneBillingCycle ? null : (formData.billingDay ? parseInt(formData.billingDay) : null),
        payment_day: formData.useZoneBillingCycle ? null : (formData.paymentDay ? parseInt(formData.paymentDay) : null),
        billing_time: formData.useZoneBillingCycle ? null : (formData.billingTime || '00:00'),
        payment_time: formData.useZoneBillingCycle ? null : (formData.paymentTime || '00:00'),
        useZoneBillingCycle: formData.useZoneBillingCycle,
        suspension_invoice_threshold: formData.suspensionInvoiceThreshold === 'global' ? null : parseInt(formData.suspensionInvoiceThreshold),
        discount_amount: formData.discountAmount ? parseFloat(formData.discountAmount) : 0.00,
        discount_months: formData.discountMonths ? parseInt(formData.discountMonths) : 0,
        is_courtesy: formData.is_courtesy === true,
        courtesy_reason: formData.courtesy_reason || ''
      };

      // Incluir también los nombres en camelCase por si acaso para evitar conflictos
      dataToSubmit.billingDay = dataToSubmit.billing_day;
      dataToSubmit.paymentDay = dataToSubmit.payment_day;
      dataToSubmit.discountAmount = dataToSubmit.discount_amount;
      dataToSubmit.discountMonths = dataToSubmit.discount_months;

      console.log('Datos a enviar al servidor:', dataToSubmit);

      const url = id
        ? `/api/clients/${id}`
        : '/api/clients';

      const response = await fetch(url, {
        method: id ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(dataToSubmit)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Error al guardar cliente');
      }

      console.log('Respuesta del servidor:', data);

      if (redirectAfterSave) {
        if (data.warning) {
          setError(`Advertencia: ${data.warning}`);
          setTimeout(() => {
            navigate('/clients/list');
          }, 3000);
        } else {
          navigate('/clients/list');
        }
      } else {
        // Guardar y seguir editando
        const savedClientId = id || (data.client ? data.client.id : data.id);
        setSuccessMessage('✅ Cambios guardados exitosamente. Puedes continuar editando.');
        setTimeout(() => setSuccessMessage(''), 4000);

        if (!id && savedClientId) {
          // Si era creación, pasar a modo edición para conservar el ID
          navigate(`/clients/edit/${savedClientId}`, { replace: true });
        }
      }
    } catch (error) {
      console.error('Error saving client:', error);
      setError(error.message || 'Error al guardar cliente');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box className="flex justify-center items-center min-h-screen">
        <CircularProgress />
      </Box>
    );
  }

  const filteredRouters = routers.filter(router => router.zone_id === formData.zoneId);
  const filteredNodes = nodes.filter(node => node.zone_id === formData.zoneId);
  const filteredSectoriales = sectoriales.filter(sectorial => sectorial.node_id === formData.nodeId && sectorial.router_id === formData.routerId);

  const handleAutoDetectConnection = async () => {
    if (!formData.ipAddress) {
      alert('Ingrese una dirección IP primero para detectar la antena Ubiquiti');
      return;
    }
    try {
      const response = await fetch(`/api/client-ap-connections/detect/${formData.ipAddress.trim()}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      if (data.found) {
        setFormData(prev => ({
          ...prev,
          macAddress: data.mac_address || prev.macAddress,
          nodeId: data.node_id || prev.nodeId,
          sectorialId: data.access_point_id || prev.sectorialId
        }));
        alert(`¡Conexión detectada! AP: ${data.ap_name} (Señal: ${data.signal_dbm || '-'} dBm)`);
      } else {
        alert(data.message || 'No se encontraron registros de antenas Ubiquiti para esta IP.');
      }
    } catch (err) {
      console.error('Error al autodetectar conexión:', err);
    }
  };

  return (
    <Box className="p-6">
      <Paper>
        <form onSubmit={handleSubmit}>
          <Box className="border-b border-gray-200">
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => setActiveTab(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
            >
              <Tab label="Datos de Conexión" />
              <Tab label="Datos del Cliente" />
              <Tab label="Configuración Avanzada" />
              <Tab label="Equipos Asignados" />
              {id && <Tab label="Servicios Complementarios y Streaming" />}
              {id && <Tab label="📄 Documentos y Fichas" />}
            </Tabs>
          </Box>

          {error && (
            <Alert severity="error" className="m-4">
              {error}
            </Alert>
          )}

          {successMessage && (
            <Alert severity="success" className="m-4">
              {successMessage}
            </Alert>
          )}

          <TabPanel value={activeTab} index={0}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Nombre del Queue"
                  name="queueName"
                  value={formData.queueName}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  freeSolo
                  options={availableIps}
                  loading={loadingIps}
                  value={formData.ipAddress}
                  onChange={(event, newValue) => {
                    setFormData(prev => ({ ...prev, ipAddress: newValue || '' }));
                  }}
                  onInputChange={(event, newInputValue) => {
                    setFormData(prev => ({ ...prev, ipAddress: newInputValue || '' }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Dirección IP *"
                      required
                      helperText={
                        loadingIps
                          ? "Buscando IPs libres..."
                          : formData.routerId
                          ? `${availableIps.length} IPs libres encontradas`
                          : "Seleccione un Router para ver IPs disponibles"
                      }
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {loadingIps ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </React.Fragment>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    fullWidth
                    label="MAC CPE"
                    name="macAddress"
                    value={formData.macAddress}
                    onChange={handleChange}
                  />
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={handleAutoDetectConnection}
                    disabled={!formData.ipAddress}
                    sx={{ whiteSpace: 'nowrap', minWidth: '135px' }}
                  >
                    Detectar AP
                  </Button>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <CoordinateSelector
                  value={formData.coordinates}
                  onChange={(newCoordinates) => handleChange({
                    target: { name: 'coordinates', value: newCoordinates }
                  })}
                  label="Coordenadas"
                  helperText="Selecciona la ubicación exacta del cliente"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Zona"
                  name="zoneId"
                  value={formData.zoneId}
                  onChange={handleZoneChange}
                  required
                >
                  {zones.map(zone => (
                    <MenuItem key={zone.id} value={zone.id}>
                      {zone.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Router"
                  name="routerId"
                  value={formData.routerId}
                  onChange={handleRouterChange}
                  required
                  disabled={!formData.zoneId}
                  helperText={!formData.zoneId ? "Selecciona una Zona primero" : ""}
                >
                  {filteredRouters.map(router => (
                    <MenuItem key={router.id} value={router.id}>
                      {router.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Torre / Nodo"
                  name="nodeId"
                  value={formData.nodeId}
                  onChange={handleNodeChange}
                  disabled={!formData.zoneId}
                  helperText={!formData.zoneId ? "Selecciona una Zona primero" : ""}
                >
                  <MenuItem value="">
                    <em>Seleccione una torre</em>
                  </MenuItem>
                  {filteredNodes.map(node => (
                    <MenuItem key={node.id} value={node.id}>
                      {node.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="AP/Sectorial"
                  name="sectorialId"
                  value={formData.sectorialId}
                  onChange={handleAPChange}
                  disabled={!formData.nodeId || !formData.routerId}
                  helperText={(!formData.nodeId || !formData.routerId) ? "Seleccione Router y Nodo primero" : ""}
                >
                  <MenuItem value="">Ninguno</MenuItem>
                  {filteredSectoriales.map(sectorial => (
                    <MenuItem key={sectorial.id} value={sectorial.id}>
                      {sectorial.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Plan de Internet"
                  name="planId"
                  value={formData.planId}
                  onChange={handleChange}
                  required
                >
                  {plans.map(plan => (
                    <MenuItem key={plan.id} value={plan.id}>
                      {plan.name} - {plan.download_speed}M/{plan.upload_speed}M
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* Botón para probar conexión */}
              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  onClick={testMikrotikConnection}
                  disabled={!formData.routerId || testing}
                  startIcon={testing ? <CircularProgress size={20} /> : null}
                >
                  {testing ? 'Probando conexión...' : 'Probar conexión al router'}
                </Button>
                {testResult !== null && (
                  <Typography
                    color={testResult ? 'success.main' : 'error'}
                    className="mt-2"
                  >
                    {testResult
                      ? '✓ Conexión exitosa'
                      : '✗ No se pudo conectar al router'}
                  </Typography>
                )}
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={activeTab} index={1}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Nombres"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Apellidos"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Tipo de Documento"
                  name="document_type"
                  value={formData.document_type || 'DNI'}
                  onChange={handleChange}
                  required
                >
                  <MenuItem value="DNI">DNI (Persona Natural)</MenuItem>
                  <MenuItem value="RUC">RUC (Empresa / B2B)</MenuItem>
                  <MenuItem value="CE">Carnet de Extranjería</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={formData.document_type === 'RUC' ? 'Número de RUC' : formData.document_type === 'CE' ? 'Número de CE' : 'Número de DNI'}
                  name="dni"
                  value={formData.dni}
                  onChange={handleChange}
                  required
                  helperText={
                    formData.document_type === 'RUC' 
                      ? 'Debe tener 11 dígitos' 
                      : formData.document_type === 'DNI' 
                        ? 'Debe tener 8 dígitos' 
                        : ''
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Button
                          size="small"
                          variant="contained"
                          color="info"
                          onClick={handleLookupDocument}
                          disabled={lookingUpDoc || !formData.dni}
                          sx={{ textTransform: 'none', fontWeight: 'bold', borderRadius: '6px' }}
                        >
                          {lookingUpDoc ? <CircularProgress size={16} color="inherit" /> : '🔍 RENIEC / SUNAT'}
                        </Button>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              {formData.document_type === 'RUC' && (
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Razón Social (Nombre de la Empresa)"
                    name="business_name"
                    value={formData.business_name || ''}
                    onChange={handleChange}
                    required
                  />
                </Grid>
              )}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Teléfono"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Correo"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                />
              </Grid>

              {/* Sección de ciclo de facturación */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Ciclo de facturación
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.useZoneBillingCycle}
                      onChange={handleChange}
                      name="useZoneBillingCycle"
                    />
                  }
                  label="Usar ciclo de facturación de la zona"
                />
              </Grid>

              {formData.useZoneBillingCycle ? (
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    {selectedZone ? (
                      <>
                        Este cliente usará el ciclo de facturación configurado para la zona "{selectedZone.name}":
                        <br />
                        - Facturación: Día {selectedZone.billing_day || 'no configurado'} a las {selectedZone.billing_time ? selectedZone.billing_time.slice(0, 5) : '00:00'}
                        <br />
                        - Vencimiento: Día {selectedZone.payment_day || 'no configurado'} a las {selectedZone.payment_time ? selectedZone.payment_time.slice(0, 5) : '00:00'}
                        <br />
                        - Corte de servicio: Día {selectedZone.cutoff_day || 'no configurado'} a las {selectedZone.cutoff_time ? selectedZone.cutoff_time.slice(0, 5) : '00:00'}
                      </>
                    ) : (
                      'Seleccione una zona para usar su ciclo de facturación'
                    )}
                  </Typography>
                </Grid>
              ) : (
                <>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <TextField
                        fullWidth
                        label="Día de facturación"
                        name="billingDay"
                        type="number"
                        value={formData.billingDay || ''}
                        onChange={handleChange}
                        inputProps={{ min: 1, max: 31 }}
                        helperText="Día del mes en que se emiten las facturas"
                      />
                      <TextField
                        fullWidth
                        label="Hora de facturación"
                        name="billingTime"
                        type="time"
                        value={formData.billingTime || '00:00'}
                        onChange={handleChange}
                        InputLabelProps={{ shrink: true }}
                        helperText="Hora del día"
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <TextField
                        fullWidth
                        label="Día de vencimiento de pago"
                        name="paymentDay"
                        type="number"
                        value={formData.paymentDay || ''}
                        onChange={handleChange}
                        inputProps={{ min: 1, max: 31 }}
                        helperText="Día del mes en que vence el pago"
                      />
                      <TextField
                        fullWidth
                        label="Hora de vencimiento"
                        name="paymentTime"
                        type="time"
                        value={formData.paymentTime || '00:00'}
                        onChange={handleChange}
                        InputLabelProps={{ shrink: true }}
                        helperText="Hora del día"
                      />
                    </Box>
                  </Grid>
                </>
              )}

              <Grid item xs={12} md={4}>
                <LocalizationProvider dateAdapter={AdapterDateFns} locale={es}>
                  <DateTimePicker
                    label="Fecha y Hora de Corte"
                    value={formData.dueDate}
                    onChange={(date) => handleChange({
                      target: { name: 'dueDate', value: date }
                    })}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} md={8}>
                <TextField
                  select
                  fullWidth
                  label="Límite de Facturas Impagas antes de Suspender"
                  name="suspensionInvoiceThreshold"
                  value={formData.suspensionInvoiceThreshold}
                  onChange={handleChange}
                  helperText="Permite definir una excepción para este cliente en lugar de la configuración global."
                >
                  <MenuItem value="global">Usar configuración de empresa (Global)</MenuItem>
                  <MenuItem value="1">1 Factura (Suspensión al primer vencimiento)</MenuItem>
                  <MenuItem value="2">2 Facturas</MenuItem>
                  <MenuItem value="3">3 Facturas</MenuItem>
                  <MenuItem value="4">4 Facturas</MenuItem>
                  <MenuItem value="5">5 Facturas</MenuItem>
                  <MenuItem value="6">6 Facturas</MenuItem>
                  <MenuItem value="0">Nunca suspender servicio (Nunca cortar)</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Dirección"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Departamento"
                  name="department"
                  value={formData.department}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Provincia"
                  name="province"
                  value={formData.province}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Distrito"
                  name="district"
                  value={formData.district}
                  onChange={handleChange}
                />
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={activeTab} index={2}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Contraseña Portal Cliente"
                  name="clientPortalPassword"
                  type={showPassword ? "text" : "password"}
                  value={formData.clientPortalPassword}
                  onChange={handleChange}
                  helperText={
                    id ?
                      "Contraseña de acceso al portal del cliente. Haga clic en el icono del ojo para visualizarla." :
                      "La contraseña debe tener al menos 6 caracteres."
                  }
                  error={!!passwordWarning}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={toggleShowPassword}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                        {passwordWarning && (
                          <Tooltip title={passwordWarning}>
                            <InfoIcon color="error" />
                          </Tooltip>
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              {/* Datos de Antena CPE */}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" fontWeight="bold" color="primary">
                  📡 Antena CPE Receptor Inalámbrico (Ubiquiti / MikroTik)
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Modelo de Antena"
                  name="antennaModel"
                  value={formData.antennaModel}
                  onChange={handleChange}
                  placeholder="Ej: Ubiquiti LiteBeam 5AC Gen2"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Dirección MAC de Antena"
                  name="antennaMac"
                  value={formData.antennaMac}
                  onChange={handleChange}
                  placeholder="Ej: 74:83:C2:AA:BB:CC"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Usuario de Antena"
                  name="antennaUser"
                  value={formData.antennaUser}
                  onChange={handleChange}
                  placeholder="Ej: admin / ubnt"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Contraseña de Antena"
                  name="antennaPassword"
                  type={showAntennaPassword ? "text" : "password"}
                  value={formData.antennaPassword}
                  onChange={handleChange}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowAntennaPassword(!showAntennaPassword)}
                          edge="end"
                        >
                          {showAntennaPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* Datos Router Wi-Fi */}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" fontWeight="bold" color="primary">
                  📶 Router Wi-Fi Principal
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Modelo Router WiFi"
                  name="wifiRouterModel"
                  value={formData.wifiRouterModel}
                  onChange={handleChange}
                  placeholder="Ej: TP-Link Archer C6 Dual Band"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Dirección MAC Router WiFi"
                  name="wifiRouterMac"
                  value={formData.wifiRouterMac}
                  onChange={handleChange}
                  placeholder="Ej: F4:F2:6D:11:22:33"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Usuario Router WiFi"
                  name="wifiRouterUser"
                  value={formData.wifiRouterUser}
                  onChange={handleChange}
                  placeholder="Ej: admin"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Contraseña Router WiFi"
                  name="wifiRouterPassword"
                  type={showWifiRouterPassword ? "text" : "password"}
                  value={formData.wifiRouterPassword}
                  onChange={handleChange}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowWifiRouterPassword(!showWifiRouterPassword)}
                          edge="end"
                        >
                          {showWifiRouterPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* Datos Red Wi-Fi Local */}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" fontWeight="bold" color="primary">
                  🔑 Red Inalámbrica Wi-Fi Local del Abonado
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="SSID WiFi (Nombre de Red)"
                  name="wifiSsid"
                  value={formData.wifiSsid}
                  onChange={handleChange}
                  placeholder="Ej: MiRedWiFi_Familia"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Contraseña WiFi"
                  name="wifiPassword"
                  type={showWifiPassword ? "text" : "password"}
                  value={formData.wifiPassword}
                  onChange={handleChange}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowWifiPassword(!showWifiPassword)}
                          edge="end"
                        >
                          {showWifiPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary">
                  Descuento Promocional 🎁⏳
                </Typography>
                <Typography variant="body2" color="textSecondary" mb={2}>
                  Aplica una reducción temporal en el monto total de los recibos generados mensualmente para este cliente.
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Descuento Mensual (S/)"
                  name="discountAmount"
                  value={formData.discountAmount}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">S/</InputAdornment>,
                  }}
                  helperText="Monto fijo a descontar de la tarifa base del plan"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Duración del Descuento (Meses)"
                  name="discountMonths"
                  value={formData.discountMonths}
                  onChange={handleChange}
                  helperText="Cantidad de meses de vigencia (se descuenta 1 mes por recibo emitido)"
                />
              </Grid>

              {/* Sección Servicio Donado / Cortesía */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Paper elevation={0} sx={{ p: 2.5, bgcolor: formData.is_courtesy ? '#f0fdf4' : '#fafafa', borderRadius: 3, border: formData.is_courtesy ? '2px solid #22c55e' : '1px solid #e2e8f0', transition: 'all 0.2s' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_courtesy || false}
                        onChange={(e) => setFormData(prev => ({ ...prev, is_courtesy: e.target.checked }))}
                        color="success"
                      />
                    }
                    label={
                      <Box sx={{ ml: 0.5 }}>
                        <Typography variant="subtitle1" fontWeight={700} color={formData.is_courtesy ? '#166534' : '#1e293b'}>
                          🎁 Servicio Donado / Cortesía (Cobro S/ 0.00)
                        </Typography>
                        <Typography variant="caption" color={formData.is_courtesy ? '#15803d' : 'text.secondary'}>
                          Si se activa, los recibos mensuales se emitirán en S/ 0.00 (Pagados) y el cliente NUNCA será suspendido por morosidad en MikroTik.
                        </Typography>
                      </Box>
                    }
                  />
                  {formData.is_courtesy && (
                    <Box sx={{ mt: 2 }}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Motivo / Entidad Beneficiaria de la Donación"
                        name="courtesy_reason"
                        placeholder="Ej: Colegio Estatal N° 108, Posta Médica, Estación de Bomberos, VIP, Colaborador"
                        value={formData.courtesy_reason || ''}
                        onChange={handleChange}
                        helperText="Ingresa la institución o justificación de este servicio donado"
                        sx={{ bgcolor: '#ffffff', borderRadius: 1 }}
                      />
                    </Box>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </TabPanel>
          <TabPanel value={activeTab} index={3}>
            {id ? (
              <ClientEquipment clientId={id} />
            ) : (
              <Typography>
                Guarde el cliente primero para poder asignar equipos.
              </Typography>
            )}
          </TabPanel>
          {id && (
            <TabPanel value={activeTab} index={4}>
              <ClientAdditionalServices clientId={id} />
            </TabPanel>
          )}
          {id && (
            <TabPanel value={activeTab} index={5}>
              <ClientDocumentsTab clientId={id} client={formData} />
            </TabPanel>
          )}

          <Box className="p-4 border-t border-gray-200 flex justify-end items-center" sx={{ gap: 2 }}>
            {id && (
              <Button
                variant="outlined"
                color="info"
                startIcon={<DescriptionIcon />}
                onClick={() => setContractModalOpen(true)}
                sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700 }}
              >
                📜 Ver Expediente / Contrato OSIPTEL
              </Button>
            )}
            <Button
              variant="outlined"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={(e) => handleSubmit(e, false)}
              disabled={saving}
              sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700 }}
            >
              {saving ? 'Guardando...' : '💾 Guardar y Seguir Editando'}
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={(e) => handleSubmit(e, true)}
              disabled={saving}
              sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700 }}
            >
              {saving ? 'Guardando...' : 'Guardar y Volver a Lista'}
            </Button>
          </Box>
        </form>
      </Paper>

      {/* Modal de Contrato */}
      {id && (
        <ContractModal
          open={contractModalOpen}
          onClose={() => setContractModalOpen(false)}
          clientId={id}
          onOpenDeactivation={() => {
            navigate('/clients/list');
          }}
        />
      )}
    </Box>
  );
};

export default AddEditClient;